import React, { useEffect, useMemo, useState } from 'react';
import { Button, Space, Input, Form, Select, Row, Col, Tag, Typography, notification, Image } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { TableCustom } from '../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AppSelect } from '../../constants/appSelect';
import { newObjectKeys, formatDateVN, DEFAULT_THUMB } from '../../utils/utility';
import { deleteCurriculum, getCurriculums } from '../../services/curriculum';
import { getAllCategories } from '../../services/category';
import isEqual from 'lodash/isEqual';

const itemName = 'Chương trình học';
const createItemLink = '/cms/quan-ly-chuong-trinh/them-chuong-trinh';
const editItemLink = '/cms/quan-ly-chuong-trinh/sua-chuong-trinh';
const { Text } = Typography;

const Curriculum = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
  });

  // helper: convert HTML to plain text and truncate to 100 chars
  const toPlain100 = (html, limit = 100) => {
    if (!html || typeof html !== 'string') return '';
    const el = document.createElement('div');
    el.innerHTML = html;
    const text = (el.textContent || el.innerText || '').trim();
    if (text.length <= limit) return text;
    return text.slice(0, limit) + '...';
  };

  // Initialize params from URL on first load
  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 20;
    const keyword = searchParams.get('keyword') || undefined;
    const statusStr = searchParams.get('status');
    const statusVal = statusStr !== null ? Number(statusStr) : -1;
    const categoryStr = searchParams.get('category_id');
    const categoryVal = categoryStr !== null ? Number(categoryStr) : -1;

    const next = { page, limit };
    if (keyword) Object.assign(next, { keyword });
    if (statusVal >= 0) Object.assign(next, { status: [statusVal] });
    if (categoryVal > 0) Object.assign(next, { category_id: categoryVal });

    setParams(next);
    formSearch.setFieldsValue({
      keyword: keyword,
      status: statusVal >= 0 ? statusVal : -1,
      category_id: categoryVal > 0 ? categoryVal : -1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push params to URL when they change
  useEffect(() => {
    if (!params) return;
    const sp = {
      page: String(params.page || 1),
      limit: String(params.limit || 20),
    };
    if (params.keyword) sp.keyword = params.keyword;
    if (Array.isArray(params.status) && params.status.length > 0 && params.status[0] >= 0) {
      sp.status = String(params.status[0]);
    }
    if (typeof params.category_id === 'number' && params.category_id > 0) {
      sp.category_id = String(params.category_id);
    }
    setSearchParams(sp, { replace: true });
  }, [params, setSearchParams]);

  const {
    data: dataCurriculums,
    isPending,
    refetch: refetchDataCurriculums,
  } = useQuery({ queryKey: ['dataCurriculums', params], queryFn: () => getCurriculums(params) });

  // categories for filter select (only type = 2: Danh mục khóa học)
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategories'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const categoryOptions = useMemo(() => {
    const all = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    // Build hierarchical options only for type=2
    const byId = {};
    const roots = [];
    const items = all.filter((c) => Number(c?.type) === 2);
    items.forEach((c) => { byId[c.id] = { ...c, children: [] }; });
    items.forEach((c) => {
      const pid = c.parent_id || 0;
      if (pid && byId[pid]) {
        byId[pid].children.push(byId[c.id]);
      } else {
        roots.push(byId[c.id]);
      }
    });
    const sortRecursive = (node) => {
      node.children.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      node.children.forEach(sortRecursive);
    };
    roots.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    roots.forEach(sortRecursive);
    const res = [{ value: -1, label: 'Tất cả' }];
    const dfs = (node, path) => {
      const currentPath = [...path, node.title || ''];
      res.push({ value: node.id, label: currentPath.join(' — ') });
      node.children.forEach((child) => dfs(child, currentPath));
    };
    roots.forEach((r) => dfs(r, []));
    return res;
  }, [allCategoriesRes]);
  const categoryMap = useMemo(() => {
    return (allCategoriesRes?.data || []).reduce((acc, cur) => {
      acc[cur?.id] = cur?.title;
      return acc;
    }, {});
  }, [allCategoriesRes]);
  const categoryPathLabelMap = useMemo(() => {
    const all = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const parentMap = all.reduce((acc, cur) => { acc[cur.id] = cur.parent_id || 0; return acc; }, {});
    const titleMap = all.reduce((acc, cur) => { acc[cur.id] = cur.title || ''; return acc; }, {});
    const cache = {};
    const toPath = (id) => {
      if (cache[id]) return cache[id];
      const parts = [];
      let cur = id;
      const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) {
        guard.add(cur);
        parts.unshift(titleMap[cur] || '');
        cur = parentMap[cur] || 0;
      }
      cache[id] = parts.filter(Boolean).join(' — ');
      return cache[id];
    };
    const map = {};
    all.forEach((c) => { map[c.id] = toPath(c.id); });
    return map;
  }, [allCategoriesRes]);

  const { mutate: mutateDeleteCurriculum } = useMutation({
    mutationFn: deleteCurriculum,
    onSuccess: async (data) => {
      if (data?.status === 1) {
        notification.success({
          message: `Xóa ${itemName} thành công!`,
        });
        refetchDataCurriculums();
      } else {
        notification.error({
          message: data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!',
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau...',
      });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.keyword) {
      Object.assign(temp, { keyword: values?.keyword });
    }
    if (values?.status >= 0) {
      Object.assign(temp, { status: [values?.status] });
    }
    if (values?.category_id > 0) {
      Object.assign(temp, { category_id: values?.category_id });
    }
    if (!isEqual(temp, params)) {
      setParams(temp);
      return;
    }
    refetchDataCurriculums();
  };

  const columns = [
    {
      title: 'STT',
      dataIndex: 'id',
      key: 'stt',
      width: 70,
      fixed: 'left',
      align: 'center',
      ellipsis: true,
      onHeaderCell: () => ({
        style: { maxWidth: 70 }
      }),
      onCell: () => ({
        style: { maxWidth: 70, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
      }),
      render: (_, __, index) => (params?.page - 1) * params?.limit + index + 1,
    },
    {
      title: 'Tên ' + itemName,
      dataIndex: 'title',
      key: 'title',
      render: (_, { title, id }) => (
        <Link to={`${editItemLink}/${id}`} className="text-[#1677FF]">
          {title}
        </Link>
      ),
      minWidth: 150,
      width: 250,
      fixed: 'left',
    },
    {
      title: 'Ảnh đại diện',
      dataIndex: 'thumb',
      key: 'thumb',
      align: 'center',
      render: (thumb) => {
        const src = typeof thumb === 'string' && thumb.trim() ? thumb : DEFAULT_THUMB;
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Image
              src={src}
              alt="thumb"
              height={48}
              style={{ width: 'auto', height: 48, objectFit: 'cover', borderRadius: 4, backgroundColor: '#f2f3f5', cursor: 'pointer' }}
              preview={{ mask: null }}
              fallback={DEFAULT_THUMB}
            />
          </div>
        );
      },
      minWidth: 100,
      width: 150,
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_id',
      key: 'category',
      render: (_, { category_id }) => <span>{categoryPathLabelMap[category_id] || '—'}</span>,
      minWidth: 180,
      width: 220,
    },
    {
      title: 'Mô tả ngắn',
      dataIndex: 'short_description',
      key: 'short_description',
      render: (sd) => <Text>{toPlain100(sd)}</Text>,
      minWidth: 200,
      width: 300,
    },
    {
      title: 'Cập nhật lần cuối',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (_, { updated_at }) => (
        <Space direction="vertical">
          {updated_at && <Text className="text-[#667085]">{formatDateVN(updated_at)}</Text>}
        </Space>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag className="px-2 py-1" color={(AppSelect.STATUS_CURRICULUM || AppSelect.STATUS)?.find((item) => item?.value === status)?.color}>
          {(AppSelect.STATUS_CURRICULUM || AppSelect.STATUS)?.find((item) => item?.value === status)?.label}
        </Tag>
      ),
      width: 70,
      fixed: 'right',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" onClick={() => navigate(`${editItemLink}/${record?.id}`)}>
            <EditOutlined />
          </Button>
          {record?.status !== 2 && (
            <Button type="text" onClick={() => mutateDeleteCurriculum(record?.id)}>
              <DeleteOutlined />
            </Button>
          )}
        </Space>
      ),
      width: 70,
      fixed: 'right',
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Danh sách {itemName}</h1>
        <Link to={createItemLink}>
          <Button type="primary">
            <PlusOutlined />
            Thêm {itemName}
          </Button>
        </Link>
      </div>

      <Form
        name="search_blocks"
        form={formSearch}
        layout="vertical"
        requiredMark={false}
        className="mb-5"
        onFinish={handleSubmitForm}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="keyword" label={<Text>Từ khóa</Text>}>
              <Input placeholder="Nhập từ khóa tìm kiếm..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="category_id" label={<Text>Danh mục</Text>} initialValue={-1}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={categoryOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...(AppSelect.STATUS_CURRICULUM || AppSelect.STATUS)]}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit">
              <SearchOutlined />
              Tìm kiếm
            </Button>
            {params &&
              Object.values(newObjectKeys(params, ['keyword', 'status', 'category_id'])).some(
                (value) => value
              ) && (
                <Button
                  htmlType="button"
                  onClick={() => {
                    formSearch.resetFields();
                    setParams({ page: 1, limit: params.limit });
                  }}
                >
                  <DeleteOutlined />
                  Xóa tìm kiếm
                </Button>
              )}
          </Col>
        </Row>
      </Form>

      <TableCustom
        columns={columns}
        dataSource={dataCurriculums?.data ?? []}
        totalItem={dataCurriculums?.total ?? 0}
        loading={isPending}
        params={params}
        setParams={setParams}
      />
    </div>
  );
};

export default Curriculum;
